/**
 * GL-DESIGN-V3-2026Q4 — Login (Instrument style).
 *
 * iPad/desktop: two-column split. Left is navy with brand mark + gold
 * eyebrow + headline. Right is tinted surface with inset fields and a
 * PrimaryPill submit.
 * iPhone: columns stack.
 */
import { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { TEST_IDS } from '@/constants/ids';
import { EyeIcon, EyeSlashIcon } from '@phosphor-icons/react';
import { PrimaryPill, InsetInput, Eyebrow, V3 } from '@/components/v3/kit';

export default function LoginPage() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [show, setShow] = useState(false);

  if (user) return <Navigate to="/" replace />;

  async function submit(e) {
    e?.preventDefault?.();
    if (busy) return;
    setBusy(true);
    setErr(null);
    const r = await login(email, password);
    setBusy(false);
    if (r.ok) nav('/');
    else setErr(r.error || 'Login failed');
  }

  return (
    <div
      style={{ background: V3.surface, minHeight: '100vh' }}
      className="flex flex-col md:flex-row"
    >
      {/* ============ Left — navy brand panel ============ */}
      <div
        className="hidden md:flex flex-col justify-between p-12 md:w-1/2 lg:w-3/5 relative overflow-hidden"
        style={{ background: V3.navy, color: '#fff' }}
      >
        <div>
          <div className="inline-flex flex-col items-start gap-6" data-testid="brand-mark">
            <img
              src="/gigline-logo-light.png?v=9"
              alt="GigLine"
              style={{
                width: 92,
                height: 92,
                borderRadius: '50%',
                boxShadow: '0 24px 48px -24px rgba(0,0,0,.6)',
              }}
            />
            <Eyebrow color={V3.gold} size={12}>GigLine Field</Eyebrow>
          </div>
        </div>
        <div className="max-w-xl">
          <h1
            style={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: 'clamp(36px, 4.5vw, 56px)',
              lineHeight: 1.05,
              letterSpacing: '-0.025em',
              color: '#fff',
            }}
          >
            Walk the floor.<br />
            Hand over the dossier.
          </h1>
          <p
            style={{
              marginTop: 20,
              fontSize: 16,
              lineHeight: 1.55,
              color: V3.ink === '#111827' ? '#c4cbe0' : '#c4cbe0', // on-navy muted
              maxWidth: 520,
            }}
          >
            Field-grade walkthroughs, CFR-backed findings, and operator-ready reports — built for Vince and the GigLine field crew.
          </p>
        </div>
        <div
          style={{
            fontFamily: V3.mono,
            fontSize: 11,
            letterSpacing: '0.14em',
            color: V3.gold,
            textTransform: 'uppercase',
            opacity: 0.75,
          }}
        >
          GL-TOOL-001 · Build 1.0 · Offline-capable PWA
        </div>
      </div>

      {/* ============ Right — sign-in column ============ */}
      <div
        className="flex-1 flex flex-col justify-center px-6 py-12 md:px-16 lg:px-24"
        style={{ background: V3.surface }}
      >
        <div className="max-w-md w-full mx-auto md:mx-0">
          {/* Mobile logo */}
          <div className="md:hidden mb-10 flex items-center gap-4">
            <img
              src="/gigline-logo.png?v=9"
              alt="GigLine"
              style={{ width: 64, height: 64, borderRadius: '50%' }}
            />
            <div>
              <div style={{ fontFamily: V3.mono, fontSize: 11, letterSpacing: '0.14em', color: V3.gold, textTransform: 'uppercase', fontWeight: 700 }}>GigLine Field</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: V3.navy, letterSpacing: '-0.02em' }}>Sign in</div>
            </div>
          </div>

          <Eyebrow color={V3.muted} size={11}>Field Operator Sign-In</Eyebrow>
          <h2
            style={{
              marginTop: 4,
              fontSize: 'clamp(28px, 3vw, 36px)',
              fontWeight: 700,
              color: V3.navy,
              letterSpacing: '-0.025em',
            }}
          >
            Sign in to GigLine
          </h2>
          <p style={{ marginTop: 10, fontSize: 14, color: V3.muted, lineHeight: 1.55 }}>
            Authorized personnel only. Sessions stay signed in for 30 days on this device.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div>
              <label
                htmlFor="email"
                style={{ display: 'block', fontSize: 13, fontWeight: 600, color: V3.navy, marginBottom: 6, paddingLeft: 6 }}
              >
                Email
              </label>
              <InsetInput
                type="email"
                value={email}
                onChange={setEmail}
                placeholder="vince@giglinecompliance.com"
                testid={TEST_IDS?.loginEmailInput || 'login-email-input'}
                autoComplete="email"
              />
            </div>
            <div>
              <label
                htmlFor="password"
                style={{ display: 'block', fontSize: 13, fontWeight: 600, color: V3.navy, marginBottom: 6, paddingLeft: 6 }}
              >
                Password
              </label>
              <div style={{ position: 'relative' }}>
                <InsetInput
                  type={show ? 'text' : 'password'}
                  value={password}
                  onChange={setPassword}
                  placeholder="••••••••"
                  testid={TEST_IDS?.loginPasswordInput || 'login-password-input'}
                  autoComplete="current-password"
                  onKeyDown={(e) => e.key === 'Enter' && submit()}
                />
                <button
                  type="button"
                  onClick={() => setShow((s) => !s)}
                  aria-label={show ? 'Hide password' : 'Show password'}
                  style={{
                    position: 'absolute',
                    right: 12,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'transparent',
                    border: 0,
                    color: V3.muted,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                  }}
                  data-testid="login-password-toggle"
                >
                  {show ? <EyeSlashIcon size={20} /> : <EyeIcon size={20} />}
                </button>
              </div>
            </div>

            {err && (
              <div
                data-testid="login-error"
                style={{
                  background: V3.tint,
                  color: V3.p1,
                  padding: '12px 16px',
                  borderRadius: 14,
                  fontSize: 13.5,
                  fontWeight: 600,
                }}
              >
                {err}
              </div>
            )}

            <div className="pt-2">
              <PrimaryPill
                icon="arrow-right"
                onClick={submit}
                height={64}
                testid={TEST_IDS?.loginSubmitButton || 'login-submit-button'}
                style={{ width: '100%', justifyContent: 'space-between' }}
              >
                {busy ? 'Signing in…' : 'Sign in'}
              </PrimaryPill>
            </div>
          </form>

          <p style={{ marginTop: 32, fontSize: 12, color: V3.muted, textAlign: 'center' }}>
            Need access? Contact Vince directly.
          </p>
        </div>
      </div>
    </div>
  );
}
